/**
 * Internationalization configuration layer.
 * Provides country, industry, and currency definitions used across signup,
 * company settings, customer address forms, and admin views.
 *
 * Adding a new country:
 *   1. Add an entry to COUNTRIES
 *   2. Add an addressConfig entry to COUNTRY_ADDRESS_CONFIG
 *   3. Map the country code to a default currency in COUNTRY_CURRENCY_MAP
 */

export const COUNTRIES = [
  { code: 'US', name: 'United States', flag: 'US' },
  { code: 'CA', name: 'Canada', flag: 'CA' },
  { code: 'TT', name: 'Trinidad & Tobago', flag: 'TT' },
  { code: 'GY', name: 'Guyana', flag: 'GY' },
];

export const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', symbol: 'TT$' },
  { code: 'GYD', name: 'Guyana Dollar', symbol: 'G$' },
];

export const COUNTRY_CURRENCY_MAP = {
  US: 'USD',
  CA: 'CAD',
  TT: 'TTD',
  GY: 'GYD',
};

/**
 * Industry definitions for signup & company settings.
 * `code` is stored in the DB. `label` is displayed in the UI.
 */
export const INDUSTRIES = [
  { code: 'plumbing', label: 'Plumbing' },
  { code: 'hvac', label: 'HVAC' },
  { code: 'electrical', label: 'Electrical' },
  { code: 'general_contracting', label: 'General Contracting' },
  { code: 'roofing', label: 'Roofing' },
  { code: 'landscaping', label: 'Landscaping' },
  { code: 'pest_control', label: 'Pest Control' },
  { code: 'cleaning', label: 'Cleaning / Janitorial' },
  { code: 'painting', label: 'Painting' },
  { code: 'fire_protection', label: 'Fire Protection' },
  { code: 'appliance_repair', label: 'Appliance Repair' },
  { code: 'locksmith', label: 'Locksmith' },
  { code: 'pool_spa', label: 'Pool & Spa' },
  { code: 'garage_door', label: 'Garage Door' },
  { code: 'solar', label: 'Solar' },
  { code: 'flooring', label: 'Flooring' },
  { code: 'other', label: 'Other' },
];

/**
 * Country-aware address field configuration.
 * Controls labels, field order, placeholder text, and the Google Places region code.
 *
 * Fields: street, unit, city, state, zip
 *   - `label`       UI label
 *   - `placeholder`  Placeholder text for input
 */
export const COUNTRY_ADDRESS_CONFIG = {
  US: {
    regionCode: 'us',
    fields: {
      street: { label: 'Street', placeholder: '123 Main St' },
      unit:   { label: 'Unit / Suite', placeholder: 'Apt 2B' },
      city:   { label: 'City', placeholder: 'New York' },
      state:  { label: 'State', placeholder: 'NY' },
      zip:    { label: 'ZIP', placeholder: '10001' },
    },
  },
  CA: {
    regionCode: 'ca',
    fields: {
      street: { label: 'Street', placeholder: '123 Main St' },
      unit:   { label: 'Unit / Suite', placeholder: 'Unit 4' },
      city:   { label: 'City', placeholder: 'Toronto' },
      state:  { label: 'Province', placeholder: 'ON' },
      zip:    { label: 'Postal Code', placeholder: 'M5V 2T6' },
    },
  },
  TT: {
    regionCode: 'tt',
    fields: {
      street: { label: 'Street', placeholder: '12 Frederick St' },
      unit:   { label: 'Unit / Suite', placeholder: '' },
      city:   { label: 'City / Town', placeholder: 'Port of Spain' },
      state:  { label: 'Region', placeholder: 'Port of Spain' },
      zip:    { label: 'Postal Code', placeholder: '' },
    },
  },
  GY: {
    regionCode: 'gy',
    fields: {
      street: { label: 'Street', placeholder: '12 Brickdam' },
      unit:   { label: 'Unit / Building', placeholder: '' },
      city:   { label: 'City / Town / Village', placeholder: 'Georgetown' },
      state:  { label: 'Region', placeholder: 'Demerara-Mahaica' },
      zip:    { label: 'Postal Code', placeholder: 'optional' },
    },
  },
};

/** Helper: get address config for a country code. Falls back to US. */
export function getAddressConfig(countryCode) {
  return COUNTRY_ADDRESS_CONFIG[countryCode] || COUNTRY_ADDRESS_CONFIG.US;
}

/** Helper: get the default currency for a country code. Falls back to USD. */
export function getDefaultCurrency(countryCode) {
  return COUNTRY_CURRENCY_MAP[countryCode] || 'USD';
}

/** Helper: look up a country name by code. */
export function getCountryName(code) {
  return COUNTRIES.find(c => c.code === code)?.name || code;
}

/** Helper: look up an industry label by code. */
export function getIndustryLabel(code) {
  return INDUSTRIES.find(i => i.code === code)?.label || code;
}

/** Helper: look up a currency symbol by code. */
export function getCurrencySymbol(code) {
  return CURRENCIES.find(c => c.code === code)?.symbol || code;
}
